import { useState } from "react";
import type { To } from "react-router";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import type { CustomerCatalogueItemSummary } from "@shared-core/data/customer-api-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  customerBasketFailure,
  type CustomerBasketFailure,
} from "@shared-core/domain/customer-basket-presentation";
import { startingAddressId } from "@shared-core/domain/customer-address-book";
import { useAddressPicker } from "@shared-core/domain/useAddressPicker";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { usePortalViewer } from "../PortalViewerContext";
import {
  STAFF_VIEW_NO_ADD_TO_BASKET,
  StaffViewReadOnlyNote,
} from "../StaffViewReadOnlyNote";
import { useCustomerBasket } from "../basket/CustomerBasketProvider";
import { BasketAddedSheet, type AddedToBasket } from "./BasketAddedSheet";
import { DeliveryAddressPicker } from "./DeliveryAddressPicker";
import { Loader2, ShoppingBasket, X } from "lucide-react";

/** One address of a line sent to several, and how many go there. */
interface SplitRow {
  addressId: string;
  quantity: number;
}

// The buy box's one order button (docs/features/customer-portal-ordering/17 section 3.1, decision B2: there
// is only "Add to basket", with Check out one click away in the sheet that follows). It adds the configured
// item to the person's basket at one of their saved delivery addresses (the default is preselected), then
// opens the "Added to your basket" sheet. The addresses are the person's own address book once the server says it
// is the rule (doc 21: they add one by typing it in, and can take one out), else the customer's own list as before. A price that has moved since the page showed it is refused once;
// the person sees the new price and adds at that. Draws nothing where this build has no basket or cannot yet
// change one (the real build until the change routes exist): a button that could only produce an error is not
// offered.
export function AddToBasket({
  itemId,
  configuration,
  versionSplit,
  versionQuantities,
  quantity,
  expectedPricePence,
  isQuoteOnRequest = false,
  speedCode,
  disabledReason,
  added,
  crossSell,
  buildPortalUrl,
}: {
  itemId: string;
  configuration: Record<string, string>;
  /** A line of several versions (the configuration's `Versions` above 1): how its quantity is shared (doc 108). */
  versionSplit?: "even" | "entered";
  versionQuantities?: number[];
  quantity: number | null;
  /** The headline the page is showing, ex VAT. */
  expectedPricePence: number | null;
  /** The page shows "Quote on request" (a paper our team prices): the line is added with no price. */
  isQuoteOnRequest?: boolean;
  /** The delivery date the price is at, by its Speed's code (never shown); null where the customer does not choose one. */
  speedCode: string | null;
  /** Why the page cannot add yet ("Choose your options", "Pricing…"); null when it can. */
  disabledReason: string | null;
  /** What the sheet shows for this configuration. */
  added: AddedToBasket;
  crossSell: { heading: string; items: CustomerCatalogueItemSummary[] };
  buildPortalUrl: (path: string) => To;
}) {
  const basket = useCustomerBasket();
  const { isStaffView } = usePortalViewer();
  const addLineUrl =
    basket?.state.status === "success"
      ? basket.state.data.actions.add_line_url
      : null;
  if (!basket || !basket.capabilities.changes || addLineUrl === null)
    // Staff look at the product as the customer sees it and never fill the customer's basket, acting or not.
    return isStaffView ? (
      <StaffViewReadOnlyNote>
        {STAFF_VIEW_NO_ADD_TO_BASKET}
      </StaffViewReadOnlyNote>
    ) : null;
  return (
    <AddToBasketControls
      addLineUrl={addLineUrl}
      itemId={itemId}
      configuration={configuration}
      versionSplit={versionSplit}
      versionQuantities={versionQuantities}
      quantity={quantity}
      expectedPricePence={expectedPricePence}
      isQuoteOnRequest={isQuoteOnRequest}
      speedCode={speedCode}
      disabledReason={disabledReason}
      added={added}
      crossSell={crossSell}
      buildPortalUrl={buildPortalUrl}
      refreshBasket={basket.refresh}
    />
  );
}

function AddToBasketControls({
  addLineUrl,
  itemId,
  configuration,
  versionSplit,
  versionQuantities,
  quantity,
  expectedPricePence,
  isQuoteOnRequest,
  speedCode,
  disabledReason,
  added,
  crossSell,
  buildPortalUrl,
  refreshBasket,
}: {
  /** The read's `add_line_url`: where an add is posted. */
  addLineUrl: string;
  itemId: string;
  configuration: Record<string, string>;
  versionSplit?: "even" | "entered";
  versionQuantities?: number[];
  quantity: number | null;
  expectedPricePence: number | null;
  isQuoteOnRequest: boolean;
  speedCode: string | null;
  disabledReason: string | null;
  added: AddedToBasket;
  crossSell: { heading: string; items: CustomerCatalogueItemSummary[] };
  buildPortalUrl: (path: string) => To;
  refreshBasket: () => void;
}) {
  const data = useCustomerDataAdapter();
  const baskets = useCustomerBasketAdapter();
  const { viewKey, sessionState } = usePortalViewer();
  const customerCode =
    sessionState.status === "success"
      ? (sessionState.data.ordering_customer_code ?? undefined)
      : undefined;

  const { state: pickerState, reload: reloadAddresses } = useAddressPicker(
    baskets,
    data,
    customerCode,
    viewKey,
  );
  const picker = pickerState.status === "ready" ? pickerState.picker : null;
  const addresses = picker?.addresses ?? [];
  const isBook = picker?.source === "address_book";
  const [chosenAddressId, setChosenAddressId] = useState<string | null>(null);
  // A choice the picker no longer holds (taken out of the book) falls back to where it starts.
  const addressId = addresses.some((a) => a.address_id === chosenAddressId)
    ? chosenAddressId
    : startingAddressId(addresses);

  // "Send to another address" (order to invoice doc 106 §3): the line split
  // across several of their addresses, a quantity each. Null: one address.
  const [rows, setRows] = useState<SplitRow[] | null>(null);
  const placed = (rows ?? []).reduce((sum, row) => sum + row.quantity, 0);
  // What the server checks (doc 106 §3.1), so Add waits until it would pass:
  // every row at least one, each address once, all of it placed.
  const splitProblem =
    rows === null
      ? null
      : new Set(rows.map((row) => row.addressId)).size < rows.length
        ? "Choose each address once."
        : rows.some((row) => row.quantity < 1)
          ? "Send at least 1 to each address."
          : null;
  const isAllPlaced = rows === null || placed === quantity;
  const isSplitPlaced = isAllPlaced && splitProblem === null;

  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<CustomerBasketFailure | null>(null);
  const [sheetPricePence, setSheetPricePence] = useState<number | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  function sendToAnotherAddress() {
    const used = new Set((rows ?? []).map((row) => row.addressId));
    const next =
      addresses.find(
        (a) => !used.has(a.address_id) && a.address_id !== addressId,
      )?.address_id ??
      addresses[0]?.address_id ??
      null;
    if (next === null) return;
    setRows((current) =>
      current === null
        ? [
            { addressId: addressId ?? next, quantity: quantity ?? 0 },
            { addressId: next, quantity: 0 },
          ]
        : [...current, { addressId: next, quantity: 0 }],
    );
  }

  function updateRow(index: number, patch: Partial<SplitRow>) {
    setRows((current) =>
      current
        ? current.map((row, i) => (i === index ? { ...row, ...patch } : row))
        : current,
    );
  }

  function removeRow(index: number) {
    if (rows === null) return;
    const left = rows.filter((_, i) => i !== index);
    if (left.length > 1) {
      setRows(left);
      return;
    }
    // Back to one address: it takes the whole quantity.
    setChosenAddressId(left[0]?.addressId ?? null);
    setRows(null);
  }

  /** Add the line at the price shown, or, for a paper our team prices, with none (`null`). */
  async function add(pricePence: number | null) {
    if (quantity === null || addressId === null || !isSplitPlaced) return;
    setAdding(true);
    setFailure(null);
    try {
      await baskets.addToBasket(addLineUrl, {
        item_id: itemId,
        configuration,
        quantity,
        ...(versionSplit ? { version_split: versionSplit } : {}),
        ...(versionQuantities ? { version_quantities: versionQuantities } : {}),
        ...(rows === null
          ? { delivery_address_id: addressId }
          : {
              delivery_lines: rows.map((row) => ({
                delivery_address_id: row.addressId,
                quantity: row.quantity,
              })),
            }),
        expected_price_pence: pricePence,
        ...(speedCode !== null && { speed_code: speedCode }),
      });
      setSheetPricePence(pricePence);
      setIsSheetOpen(true);
      refreshBasket();
    } catch (err) {
      setFailure(customerBasketFailure(err));
    } finally {
      setAdding(false);
    }
  }

  // An address taken out of the book: if it was the one chosen, the choice goes to where it starts without it.
  function onAddressRemoved(removedId: string) {
    if (addressId !== removedId) return;
    setChosenAddressId(
      startingAddressId(addresses.filter((a) => a.address_id !== removedId)),
    );
  }

  if (pickerState.status === "ready" && addresses.length === 0 && !isBook) {
    return (
      <div className="space-y-2">
        <Button size="lg" className="h-12 w-full text-base" disabled>
          <ShoppingBasket />
          Add to basket
        </Button>
        <p role="note" className="text-sm text-muted-foreground">
          Add a delivery address first. We don&apos;t have one for your account
          yet: please contact your Pollards contact and they&apos;ll add it.
        </p>
      </div>
    );
  }

  const blockedReason = disabledReason;
  const canAdd =
    blockedReason === null &&
    (expectedPricePence !== null || isQuoteOnRequest) &&
    addressId !== null &&
    isSplitPlaced &&
    !adding;
  const moved = failure?.movedPrice ?? null;
  const fieldMessage =
    failure?.fieldErrors.delivery_address_id ??
    failure?.fieldErrors.delivery_lines ??
    failure?.fieldErrors.quantity ??
    failure?.fieldErrors.speed_code ??
    null;

  return (
    <div className="space-y-3">
      {pickerState.status === "error" && (
        <div role="alert" className="space-y-2 text-sm">
          <p className="text-red-700 dark:text-red-400">
            We couldn&apos;t load your delivery addresses.
          </p>
          <Button size="sm" variant="outline" onClick={reloadAddresses}>
            Try again
          </Button>
        </div>
      )}
      {picker && rows !== null && (
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-start gap-2"
            >
              <DeliveryAddressPicker
                picker={picker}
                chosenId={row.addressId}
                onChoose={(id) => updateRow(index, { addressId: id })}
                onRemoved={onAddressRemoved}
                reload={reloadAddresses}
                canManage={false}
                disabled={adding}
                changeLabel={`Change delivery address ${index + 1}`}
              />
              <div className="space-y-1.5">
                <Label htmlFor={`add-to-basket-qty-${index}`}>Qty</Label>
                <Input
                  id={`add-to-basket-qty-${index}`}
                  inputMode="numeric"
                  className="text-right tabular-nums"
                  value={row.quantity ? row.quantity.toString() : ""}
                  disabled={adding}
                  onChange={(event) =>
                    updateRow(index, {
                      quantity:
                        Number.parseInt(
                          event.target.value.replace(/\D/g, ""),
                          10,
                        ) || 0,
                    })
                  }
                />
              </div>
              <Button
                className="mt-5"
                variant="ghost"
                size="icon"
                aria-label="Remove address"
                disabled={adding}
                onClick={() => removeRow(index)}
              >
                <X />
              </Button>
            </div>
          ))}
        </div>
      )}
      {picker && rows === null && (
        <DeliveryAddressPicker
          picker={picker}
          chosenId={addressId}
          onChoose={setChosenAddressId}
          onRemoved={onAddressRemoved}
          reload={reloadAddresses}
          canManage
          disabled={adding}
          isInvalid={Boolean(failure?.fieldErrors.delivery_address_id)}
        />
      )}
      {addresses.length > 1 && quantity !== null && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="link"
            className="h-auto px-0"
            disabled={adding}
            onClick={sendToAnotherAddress}
          >
            Send to another address
          </Button>
          {rows !== null && (
            <p
              className={`text-sm tabular-nums ${
                isAllPlaced
                  ? "text-muted-foreground"
                  : "text-red-700 dark:text-red-400"
              }`}
            >
              {`${placed.toLocaleString("en-GB")} of ${quantity.toLocaleString("en-GB")} placed`}
            </p>
          )}
        </div>
      )}
      {splitProblem !== null && (
        <p role="note" className="text-sm text-red-700 dark:text-red-400">
          {splitProblem}
        </p>
      )}

      <Button
        size="lg"
        className="h-12 w-full text-base"
        disabled={!canAdd}
        onClick={() =>
          isQuoteOnRequest
            ? void add(null)
            : expectedPricePence !== null && void add(expectedPricePence)
        }
      >
        {adding ? <Loader2 className="animate-spin" /> : <ShoppingBasket />}
        Add to basket
      </Button>
      {blockedReason && (
        <p className="text-sm text-muted-foreground">{blockedReason}</p>
      )}

      {moved && (
        <div
          role="alert"
          className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <p>{failure?.summary}</p>
          <Button
            size="sm"
            variant="outline"
            disabled={adding}
            onClick={() => void add(moved.newPricePence)}
          >
            Add at {formatQuotedPrice(moved.newPricePence)} ex VAT
          </Button>
        </div>
      )}
      {failure && !moved && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {fieldMessage ?? failure.summary}
        </p>
      )}

      {isSheetOpen && (
        <BasketAddedSheet
          added={{ ...added, totalPence: sheetPricePence ?? added.totalPence }}
          crossSell={crossSell}
          buildPortalUrl={buildPortalUrl}
          onClose={() => setIsSheetOpen(false)}
        />
      )}
    </div>
  );
}
